// Pre-computes compact stat tables from coded match events so the deep-analysis
// prompt can hand the AI ready-made aggregates instead of the full per-touch
// CSV. This slashes the input-token count (and keeps requests under provider
// rate limits) while usually improving accuracy, because the arithmetic is done
// here in JS rather than asked of the model.
//
// Most tables are derived from the normalised event rows (buildMatchEventRows),
// which already label the tracked team as "Team A" and opponents as "Team B".
// The per-rotation side-out figures are rally-based, so for those we reuse the
// per-set statsItems already computed during parsing (createStatsItem /
// addStatsItem / calculateAllStats), aggregated with the same common-team logic
// the rest of the deep analysis uses.

import { getRotationLabels } from "./Constants";
import {
    buildMatchEventRows,
    commonSideForMatch,
    findCommonTeamName,
} from "./MatchCSV";
import { addStatsItem, calculateAllStats, createStatsItem } from "./StatsItem";

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

const pct = (num, den) => {
  if (!den) {
    return "—";
  }
  return `${Math.round((num * 100) / den)}%`;
};

const signedPct = (value) => {
  if (value == null || isNaN(value)) {
    return "—";
  }
  return `${Math.round(value * 100)}%`;
};

// Renders a Markdown table. `rows` is an array of arrays (already stringified).
// Returns "" when there are no data rows so empty tables are dropped.
const mdTable = (title, headers, rows) => {
  if (!rows || rows.length === 0) {
    return "";
  }
  const head = `| ${headers.join(" | ")} |`;
  const sep = `| ${headers.map(() => "---").join(" | ")} |`;
  const body = rows.map((r) => `| ${r.join(" | ")} |`).join("\n");
  return `**${title}**\n\n${head}\n${sep}\n${body}\n`;
};

// Groups rows by a key function into a Map preserving insertion order.
const groupBy = (rows, keyFn) => {
  const map = new Map();
  for (const r of rows) {
    const k = keyFn(r);
    if (k === "" || k == null) {
      continue;
    }
    if (!map.has(k)) {
      map.set(k, []);
    }
    map.get(k).push(r);
  }
  return map;
};

// Sorts the entries of a Map by a numeric scoring function (descending).
const sortedEntries = (map, scoreFn) =>
  [...map.entries()].sort((a, b) => scoreFn(b[1]) - scoreFn(a[1]));

// ---------------------------------------------------------------------------
// Per-skill evaluation classification (normalised symbols: # + ! - / =)
// ---------------------------------------------------------------------------

const isServeAce = (r) => r.Evaluation === "#";
const isError = (r) => r.Evaluation === "=";
const isSpikeKill = (r) => r.Evaluation === "#";
const isSpikeBlocked = (r) => r.Evaluation === "/";
const isBlockPoint = (r) => r.Evaluation === "#";
// Reception/dig positive = perfect or positive.
const isPositive = (r) => r.Evaluation === "#" || r.Evaluation === "+";
const isDigSuccess = (r) => r.Evaluation !== "=" && r.Evaluation !== "";

// ---------------------------------------------------------------------------
// Rally-based team totals (for rotation + overall metrics)
// ---------------------------------------------------------------------------

// Aggregates the per-set statsItems into a single team-total statsItem for the
// tracked team and the opponent, using the same common-team resolution as the
// CSV. Returns { team, opp } where each is a statsItem (player == null), or null
// when there are no statsItems to aggregate.
function buildTeamTotals(matches, selectedGame, selectedTeam) {
  if (!matches || matches.length === 0) {
    return null;
  }
  const teamAAgg = createStatsItem(null, undefined);
  const teamBAgg = createStatsItem(null, undefined);
  const teamTotal = (items) =>
    items ? items.find((si) => si.player == null) : null;
  const commonName = findCommonTeamName(matches);

  for (const m of matches) {
    const commonSide = commonSideForMatch(m, commonName) || "A";
    let homeItem = null;
    let awayItem = null;
    if (!selectedGame || selectedGame === 0) {
      homeItem = teamTotal(m.teamA.statsItems);
      awayItem = teamTotal(m.teamB.statsItems);
    } else {
      const s = m.sets ? m.sets[selectedGame - 1] : null;
      if (s) {
        homeItem = teamTotal(s.teamAStatsItems);
        awayItem = teamTotal(s.teamBStatsItems);
      }
    }
    const aItem = commonSide === "B" ? awayItem : homeItem;
    const bItem = commonSide === "B" ? homeItem : awayItem;
    if (aItem) {
      addStatsItem(aItem, teamAAgg);
    }
    if (bItem) {
      addStatsItem(bItem, teamBAgg);
    }
  }
  calculateAllStats(teamAAgg);
  calculateAllStats(teamBAgg);

  return selectedTeam === 0
    ? { team: teamAAgg, opp: teamBAgg }
    : { team: teamBAgg, opp: teamAAgg };
}

// ---------------------------------------------------------------------------
// Section table builders. Each returns a Markdown string (possibly empty).
// `teamRows` / `oppRows` are the normalised rows for the analysed team and the
// opponent respectively.
// ---------------------------------------------------------------------------

function servingTables(teamRows) {
  const serves = teamRows.filter((r) => r.Skill === "Serve");
  if (serves.length === 0) {
    return "";
  }
  const byPlayer = groupBy(serves, (r) => r.Player);
  const playerRows = sortedEntries(byPlayer, (rs) => rs.length).map(
    ([player, rs]) => {
      const aces = rs.filter(isServeAce).length;
      const errs = rs.filter(isError).length;
      return [
        `#${player}`,
        rs.length,
        aces,
        errs,
        pct(aces, rs.length),
        pct(errs, rs.length),
      ];
    }
  );
  const byType = groupBy(serves, (r) => r.Skill_Type || "Unknown");
  const typeRows = sortedEntries(byType, (rs) => rs.length).map(
    ([type, rs]) => {
      const aces = rs.filter(isServeAce).length;
      const errs = rs.filter(isError).length;
      return [type, rs.length, aces, errs, pct(aces, rs.length), pct(errs, rs.length)];
    }
  );
  return [
    mdTable(
      "Serving by player",
      ["Server", "Serves", "Aces", "Errors", "Ace%", "Err%"],
      playerRows
    ),
    mdTable(
      "Serving by type",
      ["Serve type", "Serves", "Aces", "Errors", "Ace%", "Err%"],
      typeRows
    ),
  ]
    .filter(Boolean)
    .join("\n");
}

function receptionTables(teamRows) {
  const recs = teamRows.filter((r) => r.Skill === "Reception");
  if (recs.length === 0) {
    return "";
  }
  const byPlayer = groupBy(recs, (r) => r.Player);
  const playerRows = sortedEntries(byPlayer, (rs) => rs.length).map(
    ([player, rs]) => {
      const perfect = rs.filter((r) => r.Evaluation === "#").length;
      const positive = rs.filter((r) => r.Evaluation === "+").length;
      const ok = rs.filter((r) => r.Evaluation === "!").length;
      const neg = rs.filter((r) => r.Evaluation === "-").length;
      const poor = rs.filter((r) => r.Evaluation === "/").length;
      const err = rs.filter((r) => r.Evaluation === "=").length;
      return [
        `#${player}`,
        rs.length,
        perfect,
        positive,
        ok,
        neg,
        poor,
        err,
        pct(perfect + positive, rs.length),
        pct(err, rs.length),
      ];
    }
  );
  const byZone = groupBy(recs, (r) => r.Zone || r.Start_Zone);
  const zoneRows = sortedEntries(byZone, (rs) => rs.length).map(
    ([zone, rs]) => {
      const posCount = rs.filter(isPositive).length;
      const err = rs.filter(isError).length;
      return [`Zone ${zone}`, rs.length, pct(posCount, rs.length), pct(err, rs.length)];
    }
  );
  return [
    mdTable(
      "Reception by player (quality breakdown)",
      [
        "Receiver",
        "Total",
        "Perfect #",
        "Pos +",
        "OK !",
        "Neg -",
        "Poor /",
        "Err =",
        "Pos%",
        "Err%",
      ],
      playerRows
    ),
    mdTable(
      "Reception by zone",
      ["Zone", "Receptions", "Pos%", "Err%"],
      zoneRows
    ),
  ]
    .filter(Boolean)
    .join("\n");
}

function settingTables(teamRows) {
  const sets = teamRows.filter((r) => r.Skill === "Set");
  if (sets.length > 0) {
    const total = sets.length;
    const byPlayer = groupBy(sets, (r) => r.Player);
    const playerRows = sortedEntries(byPlayer, (rs) => rs.length).map(
      ([player, rs]) => [`#${player}`, rs.length, pct(rs.length, total)]
    );
    const byZone = groupBy(sets, (r) => r.End_Zone || r.Zone);
    const zoneRows = sortedEntries(byZone, (rs) => rs.length).map(
      ([zone, rs]) => [`Zone ${zone}`, rs.length, pct(rs.length, total)]
    );
    return [
      mdTable(
        "Set distribution by player",
        ["Setter #", "Sets", "Share"],
        playerRows
      ),
      mdTable(
        "Set distribution by target zone",
        ["Target zone", "Sets", "Share"],
        zoneRows
      ),
    ]
      .filter(Boolean)
      .join("\n");
  }

  // Fallback: no explicit Set touches were coded, so infer the setter's
  // distribution from the attacks that followed each set. Every spike implies a
  // set delivered to that attacker, at that tempo / combo / zone.
  const spikes = teamRows.filter((r) => r.Skill === "Spike");
  if (spikes.length === 0) {
    return "";
  }
  const total = spikes.length;
  const byAttacker = groupBy(spikes, (r) => r.Player);
  const attackerRows = sortedEntries(byAttacker, (rs) => rs.length).map(
    ([player, rs]) => [`#${player}`, rs.length, pct(rs.length, total)]
  );
  const byTempo = groupBy(spikes, (r) => r.Skill_Type || "Unknown");
  const tempoRows = sortedEntries(byTempo, (rs) => rs.length).map(
    ([tempo, rs]) => [tempo, rs.length, pct(rs.length, total)]
  );
  const byCombo = groupBy(spikes, (r) => r.Attack_Combo || r.Setter_Call);
  const comboRows = sortedEntries(byCombo, (rs) => rs.length)
    .slice(0, 10)
    .map(([combo, rs]) => [combo, rs.length, pct(rs.length, total)]);
  const byZone = groupBy(spikes, (r) => r.Zone || r.End_Zone);
  const zoneRows = sortedEntries(byZone, (rs) => rs.length).map(
    ([zone, rs]) => [`Zone ${zone}`, rs.length, pct(rs.length, total)]
  );
  const blocks = [
    "_No explicit set touches were coded; the distribution below is inferred from the attacks that followed each set (one attack = one set delivered)._",
    mdTable(
      "Inferred set distribution by attacker",
      ["Attacker #", "Sets (attacks)", "Share"],
      attackerRows
    ),
    mdTable(
      "Inferred set distribution by tempo",
      ["Tempo", "Sets (attacks)", "Share"],
      tempoRows
    ),
    mdTable(
      "Inferred set distribution by combo / call",
      ["Combo / call", "Sets (attacks)", "Share"],
      comboRows
    ),
    mdTable(
      "Inferred set distribution by attack zone",
      ["Attack zone", "Sets (attacks)", "Share"],
      zoneRows
    ),
  ];
  return blocks.filter(Boolean).join("\n");
}

// Builds an attack summary for a set of spike rows (attempts/kills/errors/etc.).
const attackLine = (label, rs) => {
  const att = rs.length;
  const kills = rs.filter(isSpikeKill).length;
  const errs = rs.filter(isError).length;
  const blocked = rs.filter(isSpikeBlocked).length;
  const eff = att ? (kills - errs - blocked) / att : 0;
  return [label, att, kills, errs, blocked, pct(kills, att), signedPct(eff)];
};

function attackTables(teamRows) {
  const spikes = teamRows.filter((r) => r.Skill === "Spike");
  if (spikes.length === 0) {
    return "";
  }
  const byPlayer = groupBy(spikes, (r) => r.Player);
  const playerRows = sortedEntries(byPlayer, (rs) => rs.length).map(
    ([player, rs]) => attackLine(`#${player}`, rs)
  );
  const byZone = groupBy(spikes, (r) => r.End_Zone || r.Zone);
  const zoneRows = sortedEntries(byZone, (rs) => rs.length).map(([zone, rs]) =>
    attackLine(`Zone ${zone}`, rs)
  );
  const byTempo = groupBy(spikes, (r) => r.Skill_Type || "Unknown");
  const tempoRows = sortedEntries(byTempo, (rs) => rs.length).map(
    ([tempo, rs]) => attackLine(tempo, rs)
  );
  // Rotation-specific attack breakdown (kill% and error rate per rotation).
  const rotationLabels = getRotationLabels();
  const byRotation = groupBy(spikes, (r) => r.Rotation);
  const rotationRows = [...byRotation.entries()]
    .sort((a, b) => {
      const ai = rotationLabels.indexOf(a[0]);
      const bi = rotationLabels.indexOf(b[0]);
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    })
    .map(([rotation, rs]) => {
      const att = rs.length;
      const kills = rs.filter(isSpikeKill).length;
      const errs = rs.filter(isError).length;
      const blocked = rs.filter(isSpikeBlocked).length;
      const eff = att ? (kills - errs - blocked) / att : 0;
      return [
        rotation,
        att,
        kills,
        errs,
        pct(kills, att),
        pct(errs, att),
        signedPct(eff),
      ];
    });
  const headers = ["", "Att", "Kills", "Err", "Blk", "Kill%", "Eff"];
  return [
    mdTable("Attack by player", ["Attacker", ...headers.slice(1)], playerRows),
    mdTable("Attack by target zone", ["Zone", ...headers.slice(1)], zoneRows),
    mdTable("Attack by tempo", ["Tempo", ...headers.slice(1)], tempoRows),
    mdTable(
      "Attack by rotation",
      ["Rotation", "Att", "Kills", "Err", "Kill%", "Err%", "Eff"],
      rotationRows
    ),
  ]
    .filter(Boolean)
    .join("\n");
}

function blockingTables(teamRows, oppRows) {
  const blocks = teamRows.filter((r) => r.Skill === "Block");
  const byPlayer = groupBy(blocks, (r) => r.Player);
  const playerRows = sortedEntries(byPlayer, (rs) => rs.length).map(
    ([player, rs]) => {
      const points = rs.filter(isBlockPoint).length;
      const errs = rs.filter(isError).length;
      return [`#${player}`, rs.length, points, errs];
    }
  );
  // Which opponent attack zones we successfully block (opp spikes graded "/").
  const oppSpikes = oppRows.filter((r) => r.Skill === "Spike");
  const byZone = groupBy(oppSpikes, (r) => r.End_Zone || r.Zone);
  const zoneRows = sortedEntries(byZone, (rs) => rs.length).map(
    ([zone, rs]) => {
      const stuffed = rs.filter(isSpikeBlocked).length;
      return [`Zone ${zone}`, rs.length, stuffed, pct(stuffed, rs.length)];
    }
  );
  return [
    mdTable(
      "Blocking by player",
      ["Blocker", "Touches", "Points #", "Errors ="],
      playerRows
    ),
    mdTable(
      "Opponent attacks blocked, by zone",
      ["Opp attack zone", "Opp attempts", "Stuffed", "Block%"],
      zoneRows
    ),
  ]
    .filter(Boolean)
    .join("\n");
}

function defenceTables(teamRows, oppRows) {
  const digs = teamRows.filter((r) => r.Skill === "Dig");
  const byPlayer = groupBy(digs, (r) => r.Player);
  const playerRows = sortedEntries(byPlayer, (rs) => rs.length).map(
    ([player, rs]) => {
      const ok = rs.filter(isDigSuccess).length;
      const err = rs.filter(isError).length;
      return [`#${player}`, rs.length, ok, err, pct(ok, rs.length)];
    }
  );
  const byZone = groupBy(digs, (r) => r.Zone || r.Start_Zone);
  const zoneRows = sortedEntries(byZone, (rs) => rs.length).map(
    ([zone, rs]) => {
      const ok = rs.filter(isDigSuccess).length;
      return [`Zone ${zone}`, rs.length, pct(ok, rs.length)];
    }
  );
  // Which opponent hit types are most effective against us (hardest to defend).
  const oppSpikes = oppRows.filter((r) => r.Skill === "Spike");
  const byHit = groupBy(oppSpikes, (r) => r.Type_of_Hit || "Unknown");
  const hitRows = sortedEntries(byHit, (rs) => rs.length).map(([hit, rs]) => {
    const kills = rs.filter(isSpikeKill).length;
    return [hit, rs.length, kills, pct(kills, rs.length)];
  });
  return [
    mdTable(
      "Digging by player",
      ["Defender", "Digs", "Successful", "Errors", "Success%"],
      playerRows
    ),
    mdTable("Digging by zone", ["Zone", "Digs", "Success%"], zoneRows),
    mdTable(
      "Opponent attack types faced (defensive difficulty)",
      ["Hit type", "Opp attacks", "Opp kills", "Opp kill%"],
      hitRows
    ),
  ]
    .filter(Boolean)
    .join("\n");
}

function rotationTable(totals) {
  if (!totals || !totals.team) {
    return "";
  }
  const si = totals.team;
  const labels = getRotationLabels();
  const rows = [];
  for (let i = 1; i <= 6; i++) {
    const so = si.SideOuts[i] || 0;
    const fails = si.SideOutFails[i] || 0;
    const attempts = so + fails;
    if (attempts === 0) {
      continue;
    }
    rows.push([
      labels[i - 1] || `R${i}`,
      attempts,
      signedPct((si.SideOutPercent[i] || 0) / 100),
      signedPct((si.SideOutFirstBallPercent[i] || 0) / 100),
      signedPct((si.SideOutTransitionPercent[i] || 0) / 100),
      si.BreakPoints[i] || 0,
    ]);
  }
  return mdTable(
    "Side-out & break-point by rotation",
    [
      "Rotation",
      "Receptions",
      "Side-out%",
      "First-ball%",
      "Transition%",
      "Break pts",
    ],
    rows
  );
}

function opponentTables(oppRows) {
  const spikes = oppRows.filter((r) => r.Skill === "Spike");
  if (spikes.length === 0 && oppRows.length === 0) {
    return "";
  }
  const byAttacker = groupBy(spikes, (r) => r.Player);
  const attackerRows = sortedEntries(byAttacker, (rs) => rs.length)
    .slice(0, 8)
    .map(([player, rs]) => {
      const kills = rs.filter(isSpikeKill).length;
      return [`#${player}`, rs.length, kills, pct(kills, rs.length)];
    });
  const byZone = groupBy(spikes, (r) => r.End_Zone || r.Zone);
  const zoneRows = sortedEntries(byZone, (rs) => rs.length).map(([zone, rs]) =>
    attackLine(`Zone ${zone}`, rs)
  );
  const byCombo = groupBy(spikes, (r) => r.Attack_Combo);
  const comboRows = sortedEntries(byCombo, (rs) => rs.length)
    .slice(0, 8)
    .map(([combo, rs]) => {
      const kills = rs.filter(isSpikeKill).length;
      return [combo, rs.length, kills, pct(kills, rs.length)];
    });
  const recs = oppRows.filter((r) => r.Skill === "Reception");
  const recByZone = groupBy(recs, (r) => r.Zone || r.Start_Zone);
  const recRows = sortedEntries(recByZone, (rs) => rs.length).map(
    ([zone, rs]) => {
      const posCount = rs.filter(isPositive).length;
      return [`Zone ${zone}`, rs.length, pct(posCount, rs.length)];
    }
  );
  return [
    mdTable(
      "Opponent go-to attackers",
      ["Attacker", "Attacks", "Kills", "Kill%"],
      attackerRows
    ),
    mdTable(
      "Opponent attack zones",
      ["Zone", "Att", "Kills", "Err", "Blk", "Kill%", "Eff"],
      zoneRows
    ),
    mdTable(
      "Opponent attack combinations",
      ["Combo", "Uses", "Kills", "Kill%"],
      comboRows
    ),
    mdTable(
      "Opponent serve-receive zones",
      ["Zone", "Receptions", "Pos%"],
      recRows
    ),
  ]
    .filter(Boolean)
    .join("\n");
}

function overallTables(teamRows, totals) {
  const blocks = [];
  if (totals && totals.team) {
    const si = totals.team;
    const teamRowsTable = [
      ["Side-out %", signedPct((si.SideOutPercent[0] || 0) / 100)],
      ["Break-point % (point on serve)", signedPct((si.pointPercent || 0) / 100)],
      [
        "Serve",
        `${si.ServeTotal} att, ${si.Serve3} aces, ${si.Serve0} err, eff ${signedPct(
          si.ServeEfficiency
        )}`,
      ],
      [
        "Attack",
        `${si.SpikeTotal} att, ${si.Spike3} kills, ${si.Spike0} err, ${si.SpikeBlocked} blocked, kill% ${signedPct(
          si.SpikeKillPercentage
        )}, eff ${signedPct(si.SpikeEfficiency)}`,
      ],
      ["Block points", `${si.BlockPoints}`],
      ["Dig success (avg)", si.DigAverageString || "—"],
    ];
    blocks.push(
      mdTable("Team totals", ["Metric", "Value"], teamRowsTable)
    );
  }
  // Per-set attack / serve breakdown (addresses fatigue / sets 1-3 vs 4-5).
  const spikes = teamRows.filter((r) => r.Skill === "Spike");
  const serves = teamRows.filter((r) => r.Skill === "Serve");
  const sets = [...new Set(teamRows.map((r) => r.Set).filter((s) => s !== ""))].sort(
    (a, b) => a - b
  );
  const setRows = sets.map((setNo) => {
    const sp = spikes.filter((r) => r.Set === setNo);
    const sv = serves.filter((r) => r.Set === setNo);
    const kills = sp.filter(isSpikeKill).length;
    const errs = sp.filter(isError).length;
    const blocked = sp.filter(isSpikeBlocked).length;
    const eff = sp.length ? (kills - errs - blocked) / sp.length : 0;
    const aces = sv.filter(isServeAce).length;
    const sErr = sv.filter(isError).length;
    return [
      `Set ${setNo}`,
      sp.length,
      kills,
      signedPct(eff),
      sv.length,
      aces,
      sErr,
    ];
  });
  blocks.push(
    mdTable(
      "By set",
      ["Set", "Att", "Kills", "Att eff", "Serves", "Aces", "Serve err"],
      setRows
    )
  );
  return blocks.filter(Boolean).join("\n");
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

// Short legend describing how to read the pre-computed tables, sent once.
export const kStatTableLegend = [
  "The data below is PRE-AGGREGATED from ball-by-ball coding (not raw events) to save space. Base every statement on these numbers.",
  "Evaluation symbols where shown: # perfect/point, + positive, ! ok, - negative, / poor (or attack blocked), = error/point lost.",
  "Att = attempts, Kill% = kills / attempts, Eff = (kills − errors − blocked) / attempts. Zones are court zones 1-9. Players are shown by shirt number (e.g. #7).",
  "Player numbers, rotations and zones are exactly as coded; do not invent names.",
  "Player position/role is generally NOT labelled in this data. When recommending serve targets, do not recommend serving at a player who only has a handful of receptions across the whole match (e.g. under ~6, or under ~1 per set) — that small a sample usually means a non-passing role (middle blocker or opposite) occasionally catching a stray/short serve, not a real serve-receive weakness. Only recommend serving at players with a genuine, regular reception volume.",
];

// Builds the pre-computed stat-table data block (Markdown) for the requested
// section keys. Returns "" when there is no event data. `selectedTeam` is 0 for
// Team A (the tracked team) or 1 for Team B.
export function buildSectionStatTables(
  matches,
  selectedGame,
  selectedTeam,
  sectionKeys
) {
  const rows = buildMatchEventRows(matches, selectedGame);
  if (rows.length === 0) {
    return "";
  }
  const teamLabel = selectedTeam === 0 ? "Team A" : "Team B";
  const oppLabel = selectedTeam === 0 ? "Team B" : "Team A";
  const teamRows = rows.filter((r) => r.Team === teamLabel);
  const oppRows = rows.filter((r) => r.Team === oppLabel);

  const keys =
    sectionKeys && sectionKeys.length > 0
      ? sectionKeys
      : [
          "serving",
          "reception",
          "setting",
          "attack",
          "blocking",
          "defence",
          "rotation",
          "opponent",
          "overall",
        ];

  // Only build rally-based totals when a section that needs them is requested.
  const needsTotals =
    keys.includes("rotation") || keys.includes("overall");
  const totals = needsTotals
    ? buildTeamTotals(matches, selectedGame, selectedTeam)
    : null;

  const builders = {
    serving: () => servingTables(teamRows),
    reception: () => receptionTables(teamRows),
    setting: () => settingTables(teamRows),
    attack: () => attackTables(teamRows),
    blocking: () => blockingTables(teamRows, oppRows),
    defence: () => defenceTables(teamRows, oppRows),
    rotation: () => rotationTable(totals),
    opponent: () => opponentTables(oppRows),
    overall: () => overallTables(teamRows, totals),
  };

  const out = [];
  for (const key of keys) {
    const fn = builders[key];
    if (!fn) {
      continue;
    }
    const block = fn();
    if (block && block.trim()) {
      out.push(block.trim());
    }
  }
  return out.join("\n\n");
}
